/**
 * ActivationOptOutPage — landing page for the activation drip unsubscribe
 * link embedded in drip emails and Slack DMs.
 *
 * URL contract: /activation/opt-out?t=<signed token>
 *
 * Flow:
 *   1. Read token from query string. Missing → show error.
 *   2. POST /api/activation/opt-out with the token. Idempotent.
 *   3. Render success / invalid / error.
 *
 * No app chrome — standalone page reachable from outside the authenticated
 * SPA shell. Same pattern as SlackLinkPage.
 */

import { useEffect, useMemo, useState } from "react";
import { Card } from "../ui/card.js";

type Status = "loading" | "missing_token" | "success" | "invalid" | "error";

export function ActivationOptOutPage() {
  const token = useMemo(() => {
    if (typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search).get("t");
  }, []);

  const [status, setStatus] = useState<Status>(token ? "loading" : "missing_token");

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch("/api/activation/opt-out", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ token }),
          credentials: "omit",
        });
        if (cancelled) return;
        if (res.status === 204) {
          setStatus("success");
        } else if (res.status === 400) {
          setStatus("invalid");
        } else {
          setStatus("error");
        }
      } catch {
        if (!cancelled) setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="min-h-screen w-full [background:var(--canvas-field)] flex items-center justify-center p-6">
      <Card className="max-w-md w-full p-8 space-y-4">
        <h1 className="text-xl font-semibold">YAGNI activation messages</h1>
        {status === "loading" && (
          <p className="text-sm text-text-secondary">
            One moment — updating your preferences.
          </p>
        )}
        {status === "success" && (
          <>
            <p className="text-sm">
              You're unsubscribed from the activation message series. We won't
              send any more.
            </p>
            <p className="text-sm text-text-secondary">
              You can still reach @yagni any time inside the app or via Slack.
            </p>
          </>
        )}
        {status === "missing_token" && (
          <p className="text-sm text-text-secondary">
            This unsubscribe link is missing its token. Open the link from
            your email or Slack DM directly.
          </p>
        )}
        {status === "invalid" && (
          <p className="text-sm text-text-secondary">
            This unsubscribe link is no longer valid. If you're still getting
            activation messages, reply to one of them and we'll take care of it.
          </p>
        )}
        {status === "error" && (
          <p className="text-sm text-text-secondary">
            Something went wrong. Try again in a minute, or reply to the email
            you received and we'll handle it manually.
          </p>
        )}
      </Card>
    </div>
  );
}
